import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $createTextNode,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_HIGH,
  KEY_ENTER_COMMAND,
  LexicalEditor,
  LexicalNode,
  RangeSelection,
  TextNode,
} from 'lexical';
import { useEffect } from 'react';

import { CustomParagraphNode } from '../components/nodes/CustomParagraphNode';

export function HeadingEnterPlugin(): null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand(KEY_ENTER_COMMAND, _event => handleEnterKey(editor, _event), COMMAND_PRIORITY_HIGH);
  }, [editor]);

  return null;
}

function handleEnterKey(editor: LexicalEditor, _event: KeyboardEvent | null): boolean {
  const selection = $getSelection();

  if (!isValidSelectionForHeadingOperation(selection)) {
    return false;
  }

  const node = selection.anchor.getNode() as TextNode;
  const parentNode = node.getParent() as CustomParagraphNode | null;

  if (!isHeadingNode(parentNode)) {
    return false;
  }

  const cursorOffset = selection.anchor.offset;
  const textSize = node.getTextContentSize();

  // Only handle middle-of-text cursor positions
  if (cursorOffset < textSize) {
    handleHeadingSplit(editor, node, parentNode, cursorOffset);
  }

  return false;
}

function isValidSelectionForHeadingOperation(selection: unknown): selection is RangeSelection {
  return $isRangeSelection(selection) && selection.isCollapsed();
}

function isHeadingNode(node: LexicalNode | null): node is CustomParagraphNode {
  if (!(node instanceof CustomParagraphNode)) {
    return false;
  }

  const tagName = node.__customTagName;
  return /^h[1-6]$/.test(tagName);
}

function handleHeadingSplit(
  editor: LexicalEditor,
  node: TextNode,
  parentNode: CustomParagraphNode,
  cursorOffset: number,
): void {
  // Store content for split operation
  const textContent = node.getTextContent();
  const textAfter = textContent.substring(cursorOffset);
  const tagName = parentNode.__customTagName;

  // Let default Enter behavior happen
  editor.update(() => {
    // Original node text is now trimmed by the default behavior
    node.setTextContent(textContent.substring(0, cursorOffset));
  });

  // In the next update cycle, modify the created paragraph
  editor.update(() => {
    const newSelection = $getSelection();
    if (!$isRangeSelection(newSelection)) return;

    const newNode = newSelection.anchor.getNode();
    const newParent = newNode.getParent();

    // Convert the new paragraph to a heading
    if (newParent && newParent.getType() === 'paragraph') {
      const newHeading = new CustomParagraphNode(parentNode.__customClassName, parentNode.__style, tagName);
      const newTextNode = $createTextNode(textAfter);

      newHeading.append(newTextNode);
      newParent.replace(newHeading);
      newTextNode.select(0, 0);
    }
  });
}
